// import type { AdhesePlugin, AdheseSlot, AdheseSlotOptions } from "@adhese/sdk";
// import { createAdhese } from "@adhese/sdk";
import type { EdgeOSPlugin, EdgeOSSlot, EdgeOSSlotOptions } from "@royalaholddelhaize/edgeos-web-sdk";
import { createEdgeOS } from "@royalaholddelhaize/edgeos-web-sdk";
import type { ArmpConfig, ArmpData, ArmpSlot } from "./types";

declare const window: {
  gambitConfig: ArmpConfig;
  gambit: ReturnType<typeof createEdgeOS> & {
    refresh: () => void;
    reinitSlot: (slotName: string) => ReturnType<ReturnType<typeof createEdgeOS>["addSlot"]> | null;
  };
  mapi: any;
} & Window;

const isAdEmpty = (ad: any): boolean => {
  if (typeof ad.tag === 'string' && ad.tag.includes('<title>Empty</title>')) {
    return true;
  }
  if (ad.orderName === 'Empty template' || ad.creativeName === 'Empty template') {
    return true;
  }
  return false;
};

const initEdge = () => {
  let config = window?.gambitConfig;

  const { data, slots, options } = config;

  const remapDataToParameters = (data: ArmpData) => {
    const parameterMapping = {
      pageType: "pt",
      category: "ct",
      subCategory: "ct",
      productGroup: "ct",
      searchTerm: "kw",
      userId: "mi",
      userMode: "um",
      SingleCategory: "cc"
    } as const;

    type Parameters = (typeof parameterMapping)[keyof typeof parameterMapping];

    const parameters = Object.entries(data).reduce(
      (acc, [key, value]) => {
        const mappedKey =
          parameterMapping[key as keyof typeof parameterMapping];

        if (!mappedKey) return acc;

        let valuesToAdd: string[] = [];
        if (Array.isArray(value)) {
          valuesToAdd = value.filter((item) => item !== "");
        } else if (typeof value === "string" && value !== "") {
          valuesToAdd = [String(value)];
        }

        if (valuesToAdd.length > 0) {
          if (mappedKey in acc) {
            acc[mappedKey] = [...acc[mappedKey], ...valuesToAdd];
          } else {
            acc[mappedKey] = valuesToAdd;
          }
        }

        return acc as Record<typeof mappedKey, string[]>;
      },
      {} as Record<Parameters, string[]>
    );
    if (data?.productGroup && data.productGroup.length > 0) {
      parameters.cc = [data.productGroup[data.productGroup.length - 1]];
    } else if (data?.subCategory && data.subCategory.length > 0) {
      parameters.cc = [String(data.subCategory)];
    } else if (data?.category && data.category.length > 0) {
      parameters.cc = [String(data.category)];
    }
    return parameters;
  };

  const remapSlotProps = (slot: ArmpSlot): EdgeOSSlot => {
    const { slotType, containerId } = slot;

    return {
      format: slotType,
      containingElement: containerId,
    } as EdgeOSSlot;
  };

  const initialSlots = (slots || []).map((slot) => {
    const renderMode: EdgeOSSlot["renderMode"] =
      slot.renderMode === "inline" || slot.renderMode === "iframe"
        ? slot.renderMode
        : "iframe";

    let slotName = slot.slotName || "";

    let mappedSlot: Omit<EdgeOSSlotOptions, "context" | "lazy"> = {
      ...remapSlotProps(slot),
      slot: `${data.pageType}${slotName}`,
      width: "100%",
      height: "100%",
      parameters: {},
      setup(_context, { onRender, onEmpty, onRequest }) {
        (onRequest((ad) => {
          const isEmpty = isAdEmpty(ad);

          const event = new CustomEvent("gambit:adResponseReceived", {
            detail: {
              slotName: ad.slotName,
              containerId: this.containingElement,
              orderName: ad.orderName,
              orderID: ad.orderId,
              creativeName: ad.creativeName,
              creativeID: ad.libId,
              bookingID: ad.adspaceId,
              isEmpty,
            },
          });

          window.dispatchEvent(event);

          if (isEmpty) {
            const containerId = typeof this.containingElement === 'string'
              ? this.containingElement
              : this.containingElement?.id ?? '';

            const waitForReplacementTile = () => {
              const tile = Array.from(
                document.querySelectorAll('[data-adhese-empty-slot-tracker]')
              ).find(el => {
                const ids = el.getAttribute('data-adhese-empty-slot-tracker')?.split('|') ?? [];
                return ids.includes(containerId);
              });

              if (!tile) {
                requestAnimationFrame(waitForReplacementTile);
                return;
              }

              const observer = new IntersectionObserver((entries) => {
                if (entries.some(e => e.isIntersecting) && ad.impressionCounter) {
                  const img = new Image();
                  img.src = ad.impressionCounter.toString();
                  observer.disconnect();
                }
              }, { threshold: 0 });

              observer.observe(tile);
            };

            waitForReplacementTile();
            _context?.value?.processOnEmpty();
          }
        }),
          onRender((ad) => {
            const isEmpty = isAdEmpty(ad);

            if (isEmpty) {
              // Only processOnEmpty if container still has content (not already emptied)
              const containerEl = typeof this.containingElement === 'string'
                ? document.getElementById(this.containingElement)
                : this.containingElement;

              if (containerEl && containerEl.children.length > 0) {
                _context?.value?.processOnEmpty();
              }

              // Fire slotEmpty so their side knows to clean up
              window.dispatchEvent(
                new CustomEvent("gambit:slotEmpty", {
                  detail: {
                    slotName: ad.slotName,
                    containerId: this.containingElement,
                  },
                })
              );
              return; // Don't fire adheseAdRendered for empty templates
            }

            const event = new CustomEvent("gambit:adheseAdRendered", {
              detail: {
                slotName: ad.slotName,
                containerId: this.containingElement,
                orderName: ad.orderName,
                orderID: ad.orderId,
                creativeName: ad.creativeName,
                creativeID: ad.libId,
                bookingID: ad.adspaceId,
              },
            });
            window.dispatchEvent(event);
          }),
          onEmpty(() => {
            const event = new CustomEvent("gambit:slotEmpty", {
              detail: {
                slotName: _context?.value?.name,
                containerId: this.containingElement,
              },
            });
            window.dispatchEvent(event);
          }));
      },
      renderMode: renderMode,
    };
    if (slot.position) {
      mappedSlot.parameters = {
        ps: slot.position,
      };
    }
    return mappedSlot;
  });

  // --- Plugin: tracks rendered/empty counts and sets data-rendered on containers ---
  function slotTrackerPlugin(context: any, plugin: EdgeOSPlugin) {
    const totalSlots = (slots || []).length;
    let containerIds: string[] = [];
    let respondedCount = 0;
    let renderedCount = 0;
    let emptyCount = 0;

    const getContainerId = (containingElement: string | HTMLElement): string => {
      return typeof containingElement === 'string'
        ? containingElement
        : containingElement?.id ?? '';
    };

    const setRenderedAttr = (containingElement: string | HTMLElement, rendered: boolean) => {
      const el = typeof containingElement === 'string'
        ? document.getElementById(containingElement)
        : containingElement;

      if (el) {
        el.setAttribute('data-rendered', String(rendered));
      }
    };

    const trackResponse = (containingElement: string, isEmpty: boolean) => {
      respondedCount++;
      if (isEmpty) {
        emptyCount++;
      } else {
        renderedCount++;
        containerIds.push(containingElement);
      }

      if (respondedCount === totalSlots) {
        window.dispatchEvent(
          new CustomEvent("gambit:adSummary", {
            detail: {
              total: totalSlots,
              rendered: renderedCount,
              empty: emptyCount,
              renderedContainerIds: containerIds,
            },
          })
        );
      }
    };

    plugin.hooks.onSlotCreate((slot: any) => {
      const originalSetup = slot.setup;

      slot.setup = function (ctx: any, hooks: any) {
        const originalOnRequest = hooks.onRequest;
        const originalOnRender = hooks.onRender;

        hooks.onRequest = (callback: Function) => {
          return originalOnRequest((ad: any) => {
            const isEmpty = isAdEmpty(ad);
            const containerId = getContainerId(slot.containingElement);

            setRenderedAttr(slot.containingElement, !isEmpty);
            trackResponse(containerId, isEmpty);

            return callback.call(this, ad);
          });
        };

        hooks.onRender = (callback: Function) => {
          return originalOnRender((ad: any) => {
            const isEmpty = isAdEmpty(ad);

            setRenderedAttr(slot.containingElement, !isEmpty);

            return callback.call(this, ad);
          });
        };

        if (originalSetup) {
          return originalSetup.call(this, ctx, hooks);
        }
      };

      return slot;
    });

    return {
      name: 'slotTrackerPlugin',
    };
  }

  const edgeOs = createEdgeOS({
    account: "etos",
    host: "https://zsp1.etos.nl",
    poolHost: "https://zsp2.etos.nl",
    location: `etos-nl_`,
    parameters: remapDataToParameters(data),
    initialSlots,
    debug: options.debug,
    consent: data.personalContentAdsConsent,
    refreshOnResize: false,
    plugins: [
      slotTrackerPlugin,
    ],
  });

  window.gambit = edgeOs as typeof window.gambit;

  window.gambit.refresh = () => {
    window.gambit.dispose();
    initEdge();
  };
};

(() => {
  initEdge();
})();